import { useState } from "react";
import { Spin, Dropdown } from "antd";
import { StatsModal } from "./StatsModal";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

// components/giveaways/GiveawayCard.jsx
export function GiveawayCard({
  giveaway,
  onEdit,
  onDelete,
  onViewWinners,
  onViewStats,
  onDrawWinners,
  loading = false,
}) {
  const [actionLoading, setActionLoading] = useState(false);
  const { language } = useLanguage();
  const t = giveAwayContent[language].giveawayCard;

  // ===== HELPER FUNCTIONS =====
  const formatDate = (date) => {
    if (!date) return t.dateNotAvailable;
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(date));
  };

  const formatDateTime = (date) => {
    if (!date) return t.dateNotAvailable;
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  };

  const statusConfig = {
    upcoming: {
      color: "bg-blue-100 text-blue-800",
      label: t.status.upcoming.label,
      icon: t.status.upcoming.icon,
      textColor: "text-blue-600",
    },
    active: {
      color: "bg-green-100 text-green-800",
      label: t.status.active.label,
      icon: t.status.active.icon,
      textColor: "text-green-600",
    },
    ended: {
      color: "bg-orange-100 text-orange-800",
      label: t.status.ended.label,
      icon: t.status.ended.icon,
      textColor: "text-orange-600",
    },
    drawn: {
      color: "bg-purple-100 text-purple-800",
      label: t.status.drawn.label,
      icon: t.status.drawn.icon,
      textColor: "text-purple-600",
    },
  };

  const currentStatus = statusConfig[giveaway.status] || statusConfig.upcoming;

  // ===== ACTION HANDLERS =====
  const handleAction = async (actionFn, actionName) => {
    setActionLoading(true);
    try {
      await actionFn();
    } catch (error) {
      console.error(`${t.errors[actionName]}:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      handleAction(() => onDelete(giveaway._id), "delete");
    }
  };

  const handleViewStats = () => {
    if (onViewStats) {
      handleAction(() => onViewStats(giveaway._id), "stats");
    }
  };

  const handleDrawWinners = () => {
    if (onDrawWinners) {
      handleAction(() => onDrawWinners(giveaway._id), "draw");
    }
  };

  // ===== DROPDOWN MENU =====
  const getMenuItems = () => {
    const items = [
      {
        key: "edit",
        label: t.menu.edit,
        onClick: () => onEdit(giveaway),
      },
      {
        key: "stats",
        label: t.menu.showStats,
        onClick: handleViewStats,
        disabled: !onViewStats,
      },
    ];

    if (giveaway.status === "ended") {
      items.push({
        key: "draw",
        label: t.menu.drawWinners,
        onClick: handleDrawWinners,
        disabled: !onDrawWinners,
      });
    }

    if (giveaway.status === "drawn") {
      items.push({
        key: "winners",
        label: t.menu.showWinners,
        onClick: () => onViewWinners(giveaway),
      });
    }

    const isHardDeletable = giveaway.status === "upcoming";

    items.push(
      { type: "divider" },
      isHardDeletable
        ? {
            key: "delete",
            // Falls du eine Übersetzung hast: t.menu.delete
            label: t?.menu?.delete || "Löschen",
            onClick: handleDelete,
            disabled: !onDelete, // NICHT anhand des Status disablen
            danger: true,
          }
        : {
            key: "archive",
            // Falls du eine Übersetzung hast: t.menu.archive
            label: t?.menu?.archive || "Archivieren",
            onClick: handleDelete, // gleiches Handler – Backend entscheidet Modus
            disabled: !onDelete,
            danger: false,
          }
    );

    return items;
  };

  // ===== TIME CALCULATIONS =====
  const getTimeInfo = () => {
    const now = new Date();
    const startDate = new Date(giveaway.startDate);
    const endDate = new Date(giveaway.endDate);

    if (giveaway.status === "upcoming") {
      const daysUntilStart = Math.ceil(
        (startDate - now) / (1000 * 60 * 60 * 24)
      );
      return {
        text:
          daysUntilStart > 0
            ? t.timeInfo.startsIn(daysUntilStart)
            : t.timeInfo.startsToday,
        color: "text-blue-600",
      };
    } else if (giveaway.status === "active") {
      const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return {
        text:
          daysUntilEnd > 0
            ? t.timeInfo.endsIn(daysUntilEnd)
            : t.timeInfo.endsToday,
        color: "text-green-600",
      };
    } else if (giveaway.status === "ended") {
      return {
        text: t.timeInfo.readyForDraw,
        color: "text-orange-600",
      };
    } else if (giveaway.status === "drawn") {
      return {
        text: t.timeInfo.drawnOn(formatDate(giveaway.drawnAt)),
        color: "text-purple-600",
      };
    }
    return null;
  };

  const timeInfo = getTimeInfo();
  const isDisabled = loading || actionLoading;

  return (
    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all duration-200 border border-gray-200">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {giveaway.title}
            </h3>
            {(loading || actionLoading) && <Spin size="small" />}
          </div>
          <p className="text-gray-600 text-sm line-clamp-2">
            {giveaway.description}
          </p>
        </div>

        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${currentStatus.color} ml-3`}
        >
          {currentStatus.icon} {currentStatus.label}
        </span>
      </div>

      {/* Time Info */}
      {timeInfo && (
        <div className={`text-sm font-medium mb-3 ${timeInfo.color}`}>
          {timeInfo.text}
        </div>
      )}

      {/* Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <span>📅</span>
          <span className="ml-2">
            {formatDate(giveaway.startDate)} - {formatDate(giveaway.endDate)}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <span>🏆</span>
          <span className="ml-2">{t.prizes(giveaway.prizes?.length || 0)}</span>
        </div>

        {/* Prize Preview */}
        {giveaway.prizes && giveaway.prizes.length > 0 && (
          <div className="flex items-center text-sm text-gray-600">
            <span>💰</span>
            <span className="ml-2">
              {t.mainPrize} {giveaway.prizes[0].description} (
              {giveaway.prizes[0].value})
            </span>
          </div>
        )}

        {/* Participant Count (if available) */}
        {giveaway.participantCount !== undefined && (
          <div className="flex items-center text-sm text-gray-600">
            <span>👥</span>
            <span className="ml-2">
              {t.participants(giveaway.participantCount)}
            </span>
          </div>
        )}

        {/* Draw Info */}
        {giveaway.drawnAt && (
          <div className="text-sm text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded">
            🎉 {t.drawnAt} {formatDateTime(giveaway.drawnAt)}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {/* Primary Action Button */}
        {giveaway.status === "ended" && onDrawWinners ? (
          <button
            onClick={handleDrawWinners}
            disabled={isDisabled}
            className="flex-1 px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.actions.draw}
          </button>
        ) : giveaway.status === "drawn" ? (
          <button
            onClick={() => onViewWinners(giveaway)}
            disabled={isDisabled}
            className="flex-1 px-3 py-2 text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.actions.winners}
          </button>
        ) : (
          <button
            onClick={() => onEdit(giveaway)}
            disabled={isDisabled}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.actions.edit}
          </button>
        )}

        {/* Stats Button */}
        {onViewStats && (
          <button
            onClick={handleViewStats}
            disabled={isDisabled}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t.actions.stats}
          </button>
        )}

        {/* More Actions Dropdown */}
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={["click"]}
          disabled={isDisabled}
        >
          <button
            disabled={isDisabled}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ⋮
          </button>
        </Dropdown>
      </div>

      {/* Warning for Active Giveaways */}
      {giveaway.status === "active" && (
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
          {t.activeWarning}
        </div>
      )}

      {/* Settings Info */}
      <div className="mt-3 flex flex-wrap gap-2">
        {giveaway.requiresName && (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            {t.settings.nameRequired}
          </span>
        )}
        {giveaway.requiresEmail && (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            {t.settings.emailRequired}
          </span>
        )}
        {giveaway.allowMultipleEntries && (
          <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded">
            {t.settings.multipleEntries}
          </span>
        )}
        {giveaway.autoDrawEnabled && (
          <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded">
            {t.settings.autoDraw}
          </span>
        )}
      </div>
    </div>
  );
}
